import React, { useEffect, useReducer, useState } from "react";

import openSocket from "../../services/socket-io";

import {
  Button,
  IconButton,
  makeStyles,
  Typography,
} from "@material-ui/core";

import AddIcon from "@material-ui/icons/Add";
import CallSplitIcon from "@material-ui/icons/CallSplit";
import Tooltip from "@material-ui/core/Tooltip";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { DeleteOutline, Edit } from "@material-ui/icons";
import QueueModal from "../../components/QueueModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  nameCell: { display: "flex", alignItems: "center", gap: theme.spacing(1) },
  dot: { width: 10, height: 10, borderRadius: "50%", flexShrink: 0, display: "inline-block" },
  greeting: { color: theme.palette.text.secondary, maxWidth: 460 },
  muted: { color: theme.palette.text.hint },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_QUEUES") {
    const queues = action.payload;
    const newQueues = [];

    queues.forEach((queue) => {
      const queueIndex = state.findIndex((q) => q.id === queue.id);
      if (queueIndex !== -1) {
        state[queueIndex] = queue;
      } else {
        newQueues.push(queue);
      }
    });

    return [...state, ...newQueues];
  }

  if (action.type === "UPDATE_QUEUES") {
    const queue = action.payload;
    const queueIndex = state.findIndex((u) => u.id === queue.id);

    if (queueIndex !== -1) {
      state[queueIndex] = queue;
      return [...state];
    } else {
      return [queue, ...state];
    }
  }

  if (action.type === "DELETE_QUEUE") {
    const queueId = action.payload;
    const queueIndex = state.findIndex((q) => q.id === queueId);
    if (queueIndex !== -1) {
      state.splice(queueIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Queues = () => {
  const classes = useStyles();

  const [queues, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);

  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/queue");
        dispatch({ type: "LOAD_QUEUES", payload: data });

        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const socket = openSocket();

    socket.on("queue", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUEUES", payload: data.queue });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUEUE", payload: data.queueId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleOpenQueueModal = () => {
    setQueueModalOpen(true);
    setSelectedQueue(null);
  };

  const handleCloseQueueModal = () => {
    setQueueModalOpen(false);
    setSelectedQueue(null);
  };

  const handleEditQueue = (queue) => {
    setSelectedQueue(queue);
    setQueueModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedQueue(null);
  };

  const handleDeleteQueue = async (queueId) => {
    try {
      await api.delete(`/queue/${queueId}`);
      toast.success(i18n.t("Queue deleted successfully!"));
    } catch (err) {
      toastError(err);
    }
    setSelectedQueue(null);
  };

  const columns = [
    {
      key: "name",
      label: i18n.t("queues.table.name"),
      render: (q) => (
        <div className={classes.nameCell}>
          <span className={classes.dot} style={{ backgroundColor: q.color }} />
          <Typography variant="subtitle2">{q.name}</Typography>
        </div>
      ),
    },
    {
      key: "greetingMessage",
      label: i18n.t("queues.table.greeting"),
      render: (q) => (
        <Typography variant="body2" className={classes.greeting} noWrap>
          {q.greetingMessage || <span className={classes.muted}>sem mensagem de saudação</span>}
        </Typography>
      ),
    },
    {
      key: "actions",
      label: i18n.t("queues.table.actions"),
      type: "actions",
      render: (queue) => (
        <>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => handleEditQueue(queue)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton
              size="small"
              onClick={() => {
                setSelectedQueue(queue);
                setConfirmModalOpen(true);
              }}
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          selectedQueue &&
          `${i18n.t("queues.confirmationModal.deleteTitle")} ${
            selectedQueue.name
          }?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteQueue(selectedQueue.id)}
      >
        {i18n.t("queues.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <QueueModal
        open={queueModalOpen}
        onClose={handleCloseQueueModal}
        queueId={selectedQueue?.id}
      />
      <PageHeader
        title={i18n.t("queues.title")}
        count={queues.length}
        subtitle="Filas de atendimento. Definem o roteamento das conversas e quem atende cada assunto."
        actions={
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenQueueModal}>
            {i18n.t("queues.buttons.add")}
          </Button>
        }
      />

      <DataTable
        columns={columns}
        rows={queues}
        loading={loading}
        emptyIcon={<CallSplitIcon />}
        emptyTitle="Nenhuma fila cadastrada"
        emptyDescription="Sem fila, toda conversa cai no mesmo lugar. Crie filas para separar assuntos e distribuir o atendimento."
        emptyAction={
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenQueueModal}>
            {i18n.t("queues.buttons.add")}
          </Button>
        }
      />
    </MainContainer>
  );
};

export default Queues;
